import { Component } from '@theme/component';
import { preloadImage } from '@theme/utilities';

// El endpoint vive en el proyecto de Vercel (backend), no en linkupwear.co
// (Shopify) — por eso la URL es absoluta y no relativa. Confirmado empíricamente
// contra el alias de producción real de Vercel (curl real, no supuesto).
const PREVIEW_ENDPOINT = 'https://linkupwear-webhook.vercel.app/api/preview-design';
const PHRASE_PROPERTY_NAME = 'properties[Custom phrase]';
const LINK_PROPERTY_NAME = 'properties[Link del QR]'; // explícitamente ignorado — no afecta el preview
const TEXT_COLOR_PROPERTY_NAME = 'properties[Text color]';
const PHRASE_DEBOUNCE_MS = 450;

/**
 * @typedef {object} DesignPreviewRefs
 * @property {HTMLImageElement} previewImage - La imagen que muestra el preview compuesto ("Atrás").
 * @property {HTMLImageElement} [frontImage] - La imagen estática del mockup de frente ("Frente").
 * @property {HTMLButtonElement} [frontToggle] - Botón del toggle "Frente".
 * @property {HTMLButtonElement} [backToggle] - Botón del toggle "Atrás".
 */

/**
 * Preview en vivo del diseño (frase + QR genérico) compuesto sobre el mockup del
 * color de playera seleccionado, con un toggle Frente/Atrás. Escucha, vía
 * delegación en `document`, los mismos controles que YA existen en la página (el
 * variant-picker de Color y los inputs de las properties "Frase personalizada" /
 * "Link del QR") — no crea ni duplica esa UI, salvo el propio toggle.
 *
 * @extends Component<DesignPreviewRefs>
 */
export default class DesignPreviewComponent extends Component {
  /** @type {string} */
  #currentColor = '';

  /** @type {string} */
  #currentPhrase = '';

  /** @type {string} Hex del color de texto elegido ("" = el cliente no ha tocado el selector — el server usa el default fijo del garment). */
  #currentTextColor = '';

  /** @type {'front' | 'back'} Vista actualmente visible — "Frente" nunca refleja frase/QR, solo "Atrás" lo hace. */
  #currentView = 'back';

  /** @type {number | undefined} */
  #debounceTimer;

  /** @type {boolean} Evita que dos triggers síncronos del mismo tick (p. ej. el cambio de color y el reset automático del color de texto que dispara product-text-color-swatches.js al re-sincronizar) disparen dos fetches superpuestos — el segundo abortaría al primero a medio vuelo en vez de coalescerse en uno solo. */
  #previewUpdatePending = false;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {string | undefined} */
  #objectUrl;

  /** @type {Record<string, string>} Mapa color -> URL de imagen base (blanco), leído del <script data-color-images> server-rendered. */
  #colorImages = {};

  /** @type {Record<string, string>} Mapa color -> URL del mockup de frente, leído del <script data-front-color-images> server-rendered. */
  #frontColorImages = {};

  #onColorChange = this.handleColorChange.bind(this);
  #onPropertyInput = this.handlePropertyInput.bind(this);
  #onTextColorChange = this.handleTextColorChange.bind(this);
  #onPropertyFocus = this.handlePropertyFocus.bind(this);

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('change', this.#onColorChange);
    document.addEventListener('change', this.#onTextColorChange);
    document.addEventListener('input', this.#onPropertyInput);
    // 'focus' no burbujea — 'focusin' sí, y es lo que nos deja usar delegación en
    // document igual que con el resto de los controles (el input de frase/link
    // vive fuera de este componente, como sibling del form de buy-buttons).
    document.addEventListener('focusin', this.#onPropertyFocus);

    const checkedColorInput = document.querySelector('input[name^="Color-"]:checked');
    if (checkedColorInput) {
      this.#currentColor = checkedColorInput.value;
    }

    const colorImagesScript = this.querySelector('script[data-color-images]');
    if (colorImagesScript) {
      try {
        this.#colorImages = JSON.parse(colorImagesScript.textContent ?? '{}');
      } catch (error) {
        console.error('No se pudo leer el mapa de imágenes por color:', error);
      }
    }

    const frontColorImagesScript = this.querySelector('script[data-front-color-images]');
    if (frontColorImagesScript) {
      try {
        this.#frontColorImages = JSON.parse(frontColorImagesScript.textContent ?? '{}');
      } catch (error) {
        console.error('No se pudo leer el mapa de imágenes de frente por color:', error);
      }
    }

    // Precarga las 10 imágenes (5 frente + 5 atrás) apenas conectamos, para que
    // cambiar de color después sea instantáneo — sin esto, cada color nuevo
    // dispara una descarga sin caché desde el CDN justo en el momento del clic,
    // que es lo que hacía sentir lento el cambio de color (bug reportado
    // 2026-08-14). Son 10 imágenes chicas, el costo es insignificante.
    for (const src of [...Object.values(this.#colorImages), ...Object.values(this.#frontColorImages)]) {
      preloadImage(src);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('change', this.#onColorChange);
    document.removeEventListener('change', this.#onTextColorChange);
    document.removeEventListener('input', this.#onPropertyInput);
    document.removeEventListener('focusin', this.#onPropertyFocus);
    clearTimeout(this.#debounceTimer);
    this.#abortController?.abort();
    this.#revokeObjectUrl();
  }

  /**
   * Cambia la vista a "Frente" — imagen estática del mockup, nunca refleja
   * frase/QR. Llamado por el botón del toggle (on:click="/showFront").
   */
  showFront() {
    this.#setView('front');
  }

  /**
   * Cambia la vista a "Atrás" — el preview en vivo existente. Llamado por el
   * botón del toggle (on:click="/showBack") y automáticamente al enfocar el
   * campo de frase o de link del QR.
   */
  showBack() {
    this.#setView('back');
  }

  /**
   * @param {'front' | 'back'} view
   */
  #setView(view) {
    if (this.#currentView === view) return;
    this.#currentView = view;

    const { frontImage, previewImage, frontToggle, backToggle } = this.refs;
    this.setAttribute('data-active-view', view);

    if (frontImage) frontImage.hidden = view !== 'front';
    if (previewImage) previewImage.hidden = view !== 'back';

    if (frontToggle) {
      frontToggle.classList.toggle('design-preview__toggle-button--active', view === 'front');
      frontToggle.setAttribute('aria-selected', String(view === 'front'));
    }
    if (backToggle) {
      backToggle.classList.toggle('design-preview__toggle-button--active', view === 'back');
      backToggle.setAttribute('aria-selected', String(view === 'back'));
    }
  }

  /**
   * El variant-picker de Color ya existe en la página (fuera de este componente) —
   * escuchamos el `change` nativo del radio/select real, que burbujea hasta
   * `document` sin nada especial. `event.target.value` es directamente el nombre
   * del color ("Black", "White", etc.), tal cual — NO el VariantSelectedEvent
   * custom del tema, cuyo payload solo trae un option-value-id, no el nombre.
   * @param {Event} event
   */
  handleColorChange(event) {
    const target = event.target;
    if (!(target instanceof HTMLInputElement || target instanceof HTMLSelectElement)) return;
    if (!target.name || !target.name.startsWith('Color-')) return;

    this.#currentColor = target.value;
    this.#updateFrontImage();
    // Cambio de color: instantáneo, sin debounce (a diferencia de la frase, que si
    // lleva debounce porque el usuario está tecleando).
    clearTimeout(this.#debounceTimer);
    this.#scheduleUpdatePreview();
  }

  /**
   * Actualiza la imagen de "Frente" al color actualmente seleccionado. "Frente"
   * es siempre estática — nunca compone frase/QR, así que no hay red de por
   * medio, solo cambiar el src al mockup correspondiente (ya precargado en
   * connectedCallback, así que esto pinta al instante).
   */
  #updateFrontImage() {
    const { frontImage } = this.refs;
    if (!frontImage) return;
    const src = this.#frontColorImages[this.#currentColor];
    if (src) frontImage.src = src;
  }

  /**
   * Encola un solo `updatePreview()` para el final del tick actual — colapsa
   * varios triggers síncronos del mismo click (p. ej. el cambio de color y el
   * reset automático del color de texto que dispara
   * product-text-color-swatches.js al re-sincronizarse con el nuevo garment,
   * que re-emite su propio `change`) en un solo fetch con el estado final, en
   * vez de que el segundo trigger aborte a medio vuelo el fetch que ya había
   * arrancado el primero y vuelva a empezar desde cero.
   */
  #scheduleUpdatePreview() {
    if (this.#previewUpdatePending) return;
    this.#previewUpdatePending = true;
    queueMicrotask(() => {
      this.#previewUpdatePending = false;
      this.updatePreview();
    });
  }

  /**
   * El input de "Frase personalizada" ya existe (bloque product-custom-property) —
   * escuchamos su `input` nativo vía delegación en `document`, filtrando por el
   * `name` real que Shopify le pone. El input de "Link del QR" se ignora a
   * propósito: solo se guarda para el pedido real, nunca afecta el preview (el QR
   * del preview siempre apunta a un link genérico fijo).
   * @param {Event} event
   */
  handlePropertyInput(event) {
    const target = event.target;
    if (!(target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement)) return;
    if (target.name === LINK_PROPERTY_NAME) return;
    if (target.name !== PHRASE_PROPERTY_NAME) return;

    this.#currentPhrase = target.value;
    clearTimeout(this.#debounceTimer);
    this.#debounceTimer = setTimeout(() => this.#scheduleUpdatePreview(), PHRASE_DEBOUNCE_MS);
  }

  /**
   * Auto-cambia a "Atrás" en cuanto el cliente enfoca el campo de frase o el de
   * link del QR, para que vea su personalización aplicarse sin tener que buscar
   * el toggle manualmente. Se dispara con solo enfocar, no hace falta escribir.
   * @param {FocusEvent} event
   */
  handlePropertyFocus(event) {
    const target = event.target;
    if (!(target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement)) return;
    if (target.name !== PHRASE_PROPERTY_NAME && target.name !== LINK_PROPERTY_NAME) return;

    this.showBack();
  }

  /**
   * El selector de "Color del texto" (blocks/product-text-color-swatches.liquid)
   * ya existe en la página — escuchamos su `change` nativo (radio group) vía
   * delegación en `document`, igual que el resto de los controles. Instantáneo,
   * sin debounce, igual que el cambio de garment (es una elección discreta, no
   * texto que se está tecleando). El server (api/preview-design.js) valida este
   * hex contra la whitelist del garment actual — nunca se confía en el valor tal
   * cual, así que mandar uno inválido (p. ej. justo después de cambiar de
   * garment, antes de que el swatch component reasigne) es inofensivo.
   * @param {Event} event
   */
  handleTextColorChange(event) {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;
    if (target.name !== TEXT_COLOR_PROPERTY_NAME) return;

    this.#currentTextColor = target.value;
    clearTimeout(this.#debounceTimer);
    this.#scheduleUpdatePreview();
  }

  /**
   * Dispara la composición del preview con el estado actual (color + frase), o
   * restaura la imagen base (del color actual) si la frase está vacía. Cancela
   * cualquier request anterior todavía en vuelo para que una respuesta tardía de
   * una tecla vieja nunca pueda pisar un preview más nuevo. Compone siempre sobre
   * "Atrás" — "Frente" es estática y no se ve afectada por nada de esto.
   */
  async updatePreview() {
    const phrase = this.#currentPhrase.trim();

    if (!phrase) {
      this.#abortController?.abort();
      this.resetToBaseImage();
      return;
    }

    if (!this.#currentColor) return; // aún no sabemos el color — no debería pasar, el picker siempre trae uno marcado

    this.#abortController?.abort();
    const abortController = new AbortController();
    this.#abortController = abortController;

    this.classList.add('design-preview--loading');

    try {
      const response = await fetch(PREVIEW_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phrase, color: this.#currentColor, textColor: this.#currentTextColor }),
        signal: abortController.signal,
      });

      if (!response.ok) throw new Error(`preview-design respondió ${response.status}`);

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      this.#revokeObjectUrl();
      this.#objectUrl = url;

      const { previewImage } = this.refs;
      if (previewImage) previewImage.src = url;
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') return;
      // Falla silenciosa a propósito: se queda con el último preview bueno (o la
      // imagen base) en vez de romper la página por un error de red/servidor en
      // un preview cosmético — no es checkout, no es el pedido real.
      console.error('Error generando el preview de diseño:', error);
    } finally {
      // Si ya hay un abortController más nuevo que este, esta request quedó
      // obsoleta — no le toca decidir si el estado de "cargando" se apaga.
      if (this.#abortController === abortController) {
        this.classList.remove('design-preview--loading');
      }
    }
  }

  /**
   * Restaura la imagen en blanco del color actualmente seleccionado — usa el mapa
   * color -> imagen (#colorImages) en vez de un único data-base-src fijo, para que
   * cambiar de color sin haber escrito nada también actualice el mockup.
   */
  resetToBaseImage() {
    this.#revokeObjectUrl();
    const { previewImage } = this.refs;
    if (!previewImage) return;
    const colorSrc = this.#colorImages[this.#currentColor];
    previewImage.src = colorSrc || previewImage.dataset.baseSrc;
    this.classList.remove('design-preview--loading');
  }

  #revokeObjectUrl() {
    if (this.#objectUrl) {
      URL.revokeObjectURL(this.#objectUrl);
      this.#objectUrl = undefined;
    }
  }
}

if (!customElements.get('design-preview-component')) {
  customElements.define('design-preview-component', DesignPreviewComponent);
}
